/**
 * Where an upload hears that media finished processing it, and how that news
 * folds into its job — the webapp's half of media's announcement contract.
 *
 * Media announces `attachment.processed` (and the AI text right after it) on
 * the attachment's PRIVATE channel, the bare attachmentId, which the relay
 * serves only to a signed-in viewer. An upload made through the signed-out
 * `public` route pair (vendor onboarding) is announced a second time on
 * `public:<attachmentId>`, carrying the processed outcome only: AI text is
 * never published there.
 *
 * Pure calculations — no I/O, no React.
 */

import { realtimePublicChannel } from "@birdwatch/common";

import type {
  MediaUploadEntry,
  MediaUploadJob,
  StatusItem,
} from "../_types/media-upload";
import { applyUploadEvent } from "./upload-pipeline";
import { isSettled } from "./upload-store";

type AnnouncedJob = MediaUploadJob & { attachmentId: string };

/** The relay channel media announces this job's attachment on. */
export function announcementChannelOf(
  job: Pick<AnnouncedJob, "access" | "attachmentId">,
): string {
  return job.access === "public"
    ? realtimePublicChannel(job.attachmentId)
    : job.attachmentId;
}

/** Jobs with an attachment that still expect an announcement (the outcome, or the AI text after it). */
function awaitingJobsOf(entries: MediaUploadEntry[]): AnnouncedJob[] {
  return entries
    .map((entry) => entry.job)
    .filter(
      (job): job is AnnouncedJob =>
        job.attachmentId !== null && !isSettled(job),
    );
}

/** The relay channels to subscribe to — one per attachment still expecting an announcement. */
export function activeChannelsOf(entries: MediaUploadEntry[]): string[] {
  return [...new Set(awaitingJobsOf(entries).map(announcementChannelOf))];
}

/**
 * The attachments a `/uploads/status` reconcile asks about. Always the bare
 * ids: the endpoint knows attachments, not channels, and answers a `public:`
 * name with an empty item, which would silently skip the recovery.
 */
export function activeAttachmentIdsOf(entries: MediaUploadEntry[]): string[] {
  return [...new Set(awaitingJobsOf(entries).map((job) => job.attachmentId))];
}

/**
 * Fold a terminal processing outcome into one job. A public upload's analysis
 * settles as `none` right here: its AI text is never announced where it can
 * listen, so waiting `ANALYSIS_WAIT_MS` for it would only hold its channel open.
 */
export function foldProcessed(
  job: MediaUploadJob,
  item: StatusItem,
): MediaUploadJob {
  let next = applyUploadEvent(job, { type: "status", item });
  next = applyUploadEvent(next, {
    type: "phase",
    phase: item.status === "UPLOADED" ? "done" : "failed",
  });
  if (item.status === "FAILED") {
    next = applyUploadEvent(next, {
      type: "error",
      error: "Server reported status FAILED",
    });
  }
  if (job.access === "public") {
    next = applyUploadEvent(next, { type: "analysis-none" });
  }
  return next;
}
